import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { getCurrentClinicId } from "@/lib/utils/clinic-context";
import { invalidatePatientsCache } from "@/lib/react-query/invalidatePatientsCache";
import { invalidateReportsCache } from "@/lib/react-query/invalidateReportsCache";
import { invalidateSalesCache } from "@/lib/react-query/invalidateSalesCache";
import { CURRENT_CLINIC_ID_QUERY_KEY } from "@/lib/react-query/queryKeys";
import type { RecurrenceFrequency } from "@/lib/recurrence";
import { deriveFinancialStatusWithLegacyFallback } from "@/lib/utils/appointment-financial";
import type { AppointmentDriverType } from "@/lib/utils/appointment-driver";
import i18n from "@/i18n";
import { useTranslation } from "react-i18next";

export type { ClinicAppointmentType, AppointmentTypeBehavior } from "@/hooks/useClinicAppointmentTypes";

export const BOARDING_APPOINTMENT_TYPES: string[] = [];

export type AppointmentStatus = string;

export type GroomingStatus = string;

export type AppointmentFinancialStatus = "pendente" | "pago" | "nao_cobrado";
export type AppointmentFinancialSource = "avulso" | "pacote" | "misto" | "manual";

export interface AppointmentFinancial {
  appointment_id: string;
  status: AppointmentFinancialStatus;
  source: AppointmentFinancialSource;
  owner_package_id: string | null;
  sale_id: string | null;
  is_covered_by_package: boolean;
  updated_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface Appointment {
  id: string;
  clinic_id: string;
  vet_user_id: string | null;
  owner_id: string;
  pet_id: string;
  type: string;
  status: AppointmentStatus;
  grooming_status?: GroomingStatus | null;
  starts_at: string;
  ends_at: string;
  notes: string | null;
  created_by_user_id: string;
  created_at: string;
  updated_at: string;
  is_taxi_dog?: boolean;
  taxi_dog_type?: AppointmentDriverType | null;
  recurrence_id?: string | null;
  recurrence_occurrence_date?: string | null;
  is_recurrence_exception?: boolean;
  // Boarding fields (day_care / hotel)
  is_all_day?: boolean;
  check_in_at?: string | null;
  check_out_at?: string | null;
  boarding_space_id?: string | null;
}

export interface AppointmentWithDetails extends Appointment {
  /**
   * Primary pet (kept for backwards-compat with existing UI/links).
   * This corresponds to `appointments.pet_id` when possible.
   */
  pet: Record<string, unknown> | null;
  /** All pets associated to this appointment. */
  pets?: Record<string, unknown>[];
  owner: Record<string, unknown> | null;
  vet: Record<string, unknown> | null;
  /** Planned items (no price). Financial settlement is tracked in appointment_financials. */
  appointment_items?: Array<{
    id: string;
    appointment_id: string;
    item_id: string;
    qty: number;
    title_snapshot: string;
    item?: { id: string; name: string; type?: string; sale_price?: number | null; track_by_batch?: boolean | null; track_by_expiry?: boolean | null } | null;
  }>;
  appointment_patient_packages?: Array<{
    id: string;
    mode: "new" | "existing";
    catalog_package?: {
      id: string;
      item?: { id?: string; name?: string | null } | null;
    } | null;
    patient_package?: {
      id: string;
      catalog_package?: {
        id: string;
        item?: { id?: string; name?: string | null } | null;
      } | null;
    } | null;
  }>;
  recurrence?: {
    id: string;
    frequency: RecurrenceFrequency;
    interval: number;
    weekdays?: number[] | null;
    monthday?: number | null;
    starts_on: string;
    ends_on?: string | null;
    max_occurrences?: number | null;
    active?: boolean | null;
    taxi_dog_type?: AppointmentDriverType | null;
  } | null;
  appointment_financial?: AppointmentFinancial | null;
  clinic_appointment_type?: {
    id: string;
    name: string;
    color: string;
    icon: string;
    behavior: string;
  } | null;
  linked_sale?: {
    id: string;
    status?: string | null;
    created_at?: string | null;
    sale_payments?: Array<{
      method?: string | null;
      amount?: number | null;
      received_at?: string | null;
    }> | null;
  } | null;
}

export interface AppointmentServiceItemInput {
  service_id?: string; // Optional: for legacy services table
  item_id?: string; // Optional: for items table (products, services, vaccines)
  title_snapshot: string;
  unit_price_snapshot: number;
  qty: number;
}

export interface AppointmentInput {
  clinic_id: string;
  vet_user_id?: string | null;
  owner_id: string;
  /** One or more pets (all must belong to `owner_id`). */
  pet_ids: string[];
  type: string;
  starts_at: string;
  ends_at: string;
  notes?: string | null;
  grooming_status?: GroomingStatus | null;
  status?: AppointmentStatus;
  service_items?: AppointmentServiceItemInput[];
  is_taxi_dog?: boolean;
  taxi_dog_type?: AppointmentDriverType | null;
  // Boarding fields (day_care / hotel modules)
  is_all_day?: boolean;
  boarding_space_id?: string | null;
}

export interface Veterinarian {
  id: string;
  full_name: string;
  phone: string | null;
  role: string | null;
  current_clinic_id: string | null;
}

type AppointmentPetRelationRow = {
  pet?: Record<string, unknown> | null;
};

type LinkedSale = NonNullable<AppointmentWithDetails["linked_sale"]>;

type AppointmentWithRelationsRow = Record<string, unknown> & {
  pet_id?: string | null;
  pet?: Record<string, unknown> | null;
  owner?: Record<string, unknown> | null;
  vet?: Record<string, unknown> | null;
  recurrence?: AppointmentWithDetails["recurrence"];
  appointment_items?: AppointmentWithDetails["appointment_items"];
  appointment_patient_packages?: AppointmentWithDetails["appointment_patient_packages"];
  appointment_financial?: AppointmentFinancial | AppointmentFinancial[] | null;
  linked_sale?: AppointmentWithDetails["linked_sale"] | AppointmentWithDetails["linked_sale"][] | null;
  appointment_pets?: AppointmentPetRelationRow[] | null;
};

const extractPetIdsFromAppointmentPets = (appointmentPets: unknown): string[] => {
  if (!Array.isArray(appointmentPets)) return [];
  const ids = appointmentPets
    .map((row) => {
      if (!row || typeof row !== "object") return null;
      if (!("pet_id" in row)) return null;
      const petId = (row as { pet_id?: unknown }).pet_id;
      return typeof petId === "string" && petId.trim() ? petId : null;
    })
    .filter((value): value is string => Boolean(value));
  return Array.from(new Set(ids));
};

const isActivePetRecord = (pet: unknown): pet is Record<string, unknown> => {
  return Boolean(
    pet &&
    typeof pet === "object" &&
    (!("deleted_at" in pet) || !(pet as { deleted_at?: unknown }).deleted_at),
  );
};

const normalizeAppointmentFinancial = (
  value: AppointmentWithRelationsRow["appointment_financial"],
): AppointmentFinancial | null => {
  if (Array.isArray(value)) {
    return (value[0] as AppointmentFinancial | undefined) ?? null;
  }
  return (value as AppointmentFinancial | null | undefined) ?? null;
};

const normalizeLinkedSale = ({
  value,
}: {
  value: AppointmentWithRelationsRow["linked_sale"];
}): AppointmentWithDetails["linked_sale"] => {
  const sales = Array.isArray(value)
    ? value
    : value
      ? [value]
      : [];

  if (sales.length === 0) return null;

  return [...sales]
    .sort((left, right) => {
      const leftCancelled = (left?.status === "CANCELADA" || left?.status === "DEVOLVIDA") ? 1 : 0;
      const rightCancelled = (right?.status === "CANCELADA" || right?.status === "DEVOLVIDA") ? 1 : 0;
      if (leftCancelled !== rightCancelled) return leftCancelled - rightCancelled;
      const leftTime = left?.created_at ? new Date(left.created_at).getTime() : 0;
      const rightTime = right?.created_at ? new Date(right.created_at).getTime() : 0;
      return rightTime - leftTime;
    })[0] ?? null;
};

const isCancelledSale = (sale: LinkedSale): boolean =>
  sale.status === "CANCELADA" || sale.status === "DEVOLVIDA";

const selectPreferredLinkedSale = ({
  sales,
}: {
  sales: LinkedSale[];
}): AppointmentWithDetails["linked_sale"] => {
  if (sales.length === 0) return null;

  // Prefer active (non-cancelled) sales; within each group, pick the most recent.
  return [...sales].sort((left, right) => {
    const leftCancelled = isCancelledSale(left) ? 1 : 0;
    const rightCancelled = isCancelledSale(right) ? 1 : 0;
    if (leftCancelled !== rightCancelled) return leftCancelled - rightCancelled;
    const leftTime = left.created_at ? new Date(left.created_at).getTime() : 0;
    const rightTime = right.created_at ? new Date(right.created_at).getTime() : 0;
    return rightTime - leftTime;
  })[0] ?? null;
};

const SALES_BATCH_SIZE = 40;

const fetchLinkedSalesByAppointmentIds = async (
  appointmentIds: string[],
): Promise<Record<string, LinkedSale>> => {
  const uniqueAppointmentIds = Array.from(new Set(appointmentIds)).filter(Boolean);
  if (uniqueAppointmentIds.length === 0) return {};

  const sales: Array<LinkedSale & { appointment_id: string | null }> = [];

  for (let index = 0; index < uniqueAppointmentIds.length; index += SALES_BATCH_SIZE) {
    const batchIds = uniqueAppointmentIds.slice(index, index + SALES_BATCH_SIZE);
    const { data, error } = await supabase
      .from("sales")
      .select("id, status, appointment_id, created_at, sale_payments(method, amount, received_at)")
      .in("appointment_id", batchIds);

    if (error) throw error;
    sales.push(...((data ?? []) as Array<LinkedSale & { appointment_id: string | null }>));
  }

  const salesByAppointmentId = sales.reduce<Record<string, LinkedSale[]>>((acc, sale) => {
    if (!sale.appointment_id) return acc;
    if (!acc[sale.appointment_id]) acc[sale.appointment_id] = [];
    acc[sale.appointment_id].push(sale);
    return acc;
  }, {});

  return Object.fromEntries(
    Object.entries(salesByAppointmentId).map(([appointmentId, appointmentSales]) => [
      appointmentId,
      appointmentSales.sort((left, right) => {
        const leftCancelled = isCancelledSale(left) ? 1 : 0;
        const rightCancelled = isCancelledSale(right) ? 1 : 0;
        if (leftCancelled !== rightCancelled) return leftCancelled - rightCancelled;
        const leftTime = left.created_at ? new Date(left.created_at).getTime() : 0;
        const rightTime = right.created_at ? new Date(right.created_at).getTime() : 0;
        return rightTime - leftTime;
      })[0],
    ]),
  );
};

const hydrateAppointmentFinancialFromLinkedSale = (
  appointment: AppointmentWithDetails,
): AppointmentWithDetails => {
  const derivedStatus = deriveFinancialStatusWithLegacyFallback(
    appointment.appointment_financial,
    {
      status: appointment.status,
      grooming_status: appointment.grooming_status,
    },
    appointment.linked_sale,
  );

  if (derivedStatus !== "pago") {
    return appointment;
  }

  return {
    ...appointment,
    appointment_financial: {
      appointment_id: appointment.id,
      status: "pago",
      source: appointment.appointment_financial?.source ?? "avulso",
      owner_package_id: appointment.appointment_financial?.owner_package_id ?? null,
      sale_id: appointment.linked_sale?.id ?? appointment.appointment_financial?.sale_id ?? null,
      is_covered_by_package: appointment.appointment_financial?.is_covered_by_package ?? false,
      updated_by: appointment.appointment_financial?.updated_by ?? null,
      created_at: appointment.appointment_financial?.created_at ?? new Date().toISOString(),
      updated_at: appointment.appointment_financial?.updated_at ?? new Date().toISOString(),
    },
  };
};

const normalizeAppointmentPets = (appointmentPets: AppointmentWithRelationsRow["appointment_pets"]) => {
  return (appointmentPets ?? [])
    .map((row) => row?.pet)
    .filter(isActivePetRecord);
};

const normalizeAppointmentRow = (
  row: AppointmentWithRelationsRow,
  options?: { includeItems?: boolean },
): AppointmentWithDetails | null => {
  const relationPets = normalizeAppointmentPets(row.appointment_pets);
  const directPet = isActivePetRecord(row.pet) ? row.pet : null;
  const pets = relationPets.length > 0
    ? relationPets
    : directPet
      ? [directPet]
      : [];
  const owner = row.owner ?? null;
  const appointmentFinancial = normalizeAppointmentFinancial(row.appointment_financial);

  if (pets.length === 0 || !owner) return null;

  const primary =
    pets.find((pet) => pet.id === row.pet_id) ??
    pets[0] ??
    null;

  return {
    ...(row as unknown as AppointmentWithDetails),
    pets,
    pet: primary,
    owner,
    vet: row.vet ?? null,
    recurrence: row.recurrence ?? null,
    appointment_items: options?.includeItems ? row.appointment_items : undefined,
    appointment_patient_packages: options?.includeItems ? row.appointment_patient_packages : undefined,
    appointment_financial: appointmentFinancial,
    linked_sale: normalizeLinkedSale({
      value: row.linked_sale,
    }),
  };
};

const normalizeDetailedAppointmentRow = (row: AppointmentWithRelationsRow): AppointmentWithDetails | null => {
  const appointmentFinancial = normalizeAppointmentFinancial(row.appointment_financial);
  const relationPets = (row.appointment_pets ?? [])
    .map((relation) => {
      const pet = relation?.pet;
      if (!isActivePetRecord(pet)) return null;
      const petCustomer = pet.pet_customer;
      const petNotes = Array.isArray(petCustomer)
        ? (petCustomer[0] as { notes?: string | null } | undefined)?.notes
        : (petCustomer as { notes?: string | null } | null | undefined)?.notes;
      return {
        ...pet,
        notes: petNotes || null,
      };
    })
    .filter((pet) => pet !== null);
  const directPet = isActivePetRecord(row.pet) ? row.pet : null;
  const pets = relationPets.length > 0
    ? relationPets
    : directPet
      ? [directPet]
      : [];

  if (pets.length === 0 || !row.owner) return null;

  const primary = pets.find((pet) => pet.id === row.pet_id) ?? pets[0] ?? null;

  return {
    ...(row as unknown as AppointmentWithDetails),
    pets,
    pet: primary,
    owner: row.owner ?? null,
    recurrence: row.recurrence ?? null,
    appointment_financial: appointmentFinancial,
    linked_sale: normalizeLinkedSale({
      value: row.linked_sale,
    }),
  };
};

export const useCurrentClinicId = () => {
  return useQuery({
    queryKey: CURRENT_CLINIC_ID_QUERY_KEY,
    queryFn: () => getCurrentClinicId(),
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

export const useVeterinarians = (clinicId?: string) => {
  return useQuery({
    queryKey: ["veterinarians", clinicId],
    queryFn: async () => {
      // If clinicId wasn't provided, fall back to current user's selected clinic.
      const effectiveClinicId = clinicId ?? (await getCurrentClinicId().catch(() => null));

      if (!effectiveClinicId) return [] as Veterinarian[];

      // Use RPC to bypass profiles RLS while still validating clinic access.
      const { data, error } = await supabase.rpc("get_clinic_professionals", {
        p_clinic_id: effectiveClinicId,
      }) as { data: Veterinarian[] | null; error: unknown };

      if (error) throw error;

      return (data ?? []) as Veterinarian[];
    },
  });
};

/** Lighter select for calendar/flowboard list; details loaded by useAppointment(id) in modal. */
const APPOINTMENTS_LIST_SELECT: string = `
  *,
  clinic_appointment_type:clinic_appointment_types(id, name, color, icon, behavior),
  pet:pets!appointments_pet_id_fkey(id, name, record_number, deleted_at, breed:breeds(id, name)),
  appointment_financial:appointment_financials(
    appointment_id,
    status,
    source,
    owner_package_id,
    sale_id,
    is_covered_by_package,
    updated_by,
    created_at,
    updated_at
  ),
  linked_sale:sales!sales_appointment_id_fkey(
    id,
    status,
    created_at,
    sale_payments(
      method,
      amount,
      received_at
    )
  ),
  recurrence:recurrence(id, frequency, interval, weekdays, monthday, starts_on, ends_on, max_occurrences, active, taxi_dog_type),
  appointment_pets(
    pet:pets(
      id, name, record_number, deleted_at,
      breed:breeds(id, name),
      pet_conditions(condition_id, conditions(id, name, type))
    )
  ),
  owner:owners!inner(
    id, name, deleted_at,
    phones:owners_phones(id, phone_number, is_primary, is_whatsapp, deleted_at),
    addresses:owners_addresses(id, street, number, neighborhood, complement, zip_code, is_primary, deleted_at, city:cities(id, name, state:states(id, name, code)))
  ),
  vet:profiles!appointments_vet_user_id_fkey(id, full_name, avatar_url),
  appointment_items(
    id,
    appointment_id,
    item_id,
    qty,
    title_snapshot,
    item:items(id, name, type)
  ),
  appointment_patient_packages(
    id,
    mode,
    catalog_package:catalog_packages(
      id,
      item:items(id, name)
    ),
    patient_package:patient_packages(
      id,
      catalog_package:catalog_packages(
        id,
        item:items(id, name)
      )
    )
  )
`;

/** Full select including phones, emails, addresses, appointment_items (for detail view). */
const APPOINTMENTS_FULL_SELECT: string = `
  *,
  clinic_appointment_type:clinic_appointment_types(id, name, color, icon, behavior),
  pet:pets!appointments_pet_id_fkey(*, breed:breeds(id, name), pet_conditions(condition_id, conditions(id, name, type))),
  appointment_financial:appointment_financials(
    appointment_id,
    status,
    source,
    owner_package_id,
    sale_id,
    is_covered_by_package,
    updated_by,
    created_at,
    updated_at
  ),
  linked_sale:sales!sales_appointment_id_fkey(
    id,
    status,
    created_at,
    sale_payments(
      method,
      amount,
      received_at
    )
  ),
  recurrence:recurrence(id, frequency, interval, weekdays, monthday, starts_on, ends_on, max_occurrences, active, taxi_dog_type),
  appointment_pets(
    pet:pets(*, breed:breeds(id, name), pet_conditions(condition_id, conditions(id, name, type)))
  ),
  owner:owners!inner(
    *,
    phones:owners_phones(*),
    emails:owners_emails(*),
    addresses:owners_addresses(*, city:cities(id, name, state:states(id, name, code)))
  ),
  vet:profiles!appointments_vet_user_id_fkey(id, full_name, avatar_url),
  appointment_items(
    id,
    appointment_id,
    item_id,
    qty,
    title_snapshot,
    created_at,
    updated_at,
    item:items(id, name, type, sale_price, track_by_batch, track_by_expiry)
  )
`;

/** Select optimized for CSV export (includes owner contacts + addresses). */
const APPOINTMENTS_CSV_SELECT: string = `
  *,
  clinic_appointment_type:clinic_appointment_types(id, name, color, icon, behavior),
  pet:pets!appointments_pet_id_fkey(id, name, deleted_at),
  appointment_financial:appointment_financials(
    appointment_id,
    status,
    source,
    owner_package_id,
    sale_id,
    is_covered_by_package,
    updated_by,
    created_at,
    updated_at
  ),
  appointment_items(
    id,
    appointment_id,
    item_id,
    qty,
    title_snapshot,
    item:items(id, name, type)
  ),
  appointment_patient_packages(
    id,
    mode,
    catalog_package:catalog_packages(
      id,
      item:items(id, name)
    ),
    patient_package:patient_packages(
      id,
      catalog_package:catalog_packages(
        id,
        item:items(id, name)
      )
    )
  ),
  recurrence:recurrence(id, frequency, interval, weekdays, monthday, starts_on, ends_on, max_occurrences, active, taxi_dog_type),
  appointment_pets(
    pet:pets(id, name, deleted_at)
  ),
  owner:owners!inner(
    id,
    name,
    deleted_at,
    phones:owners_phones(phone_number, is_primary, deleted_at),
    emails:owners_emails(email, is_primary, deleted_at),
    addresses:owners_addresses(
      street,
      number,
      neighborhood,
      zip_code,
      complement,
      is_primary,
      deleted_at,
      city:cities(id, name, state:states(id, name, code))
    )
  ),
  vet:profiles!appointments_vet_user_id_fkey(id, full_name, avatar_url)
`;

export interface UseAppointmentsOptions {
  /** "list" = slimmer payload for calendar/flowboard; "full" = full details (default). */
  variant?: "list" | "full";
  /** Allow fetching without filtering by a selected clinic. */
  allowWithoutClinic?: boolean;
  /** Quando fornecido (não vazio), filtra por várias clínicas (sobrepõe o clinicId único). */
  clinicIds?: string[];
  /** When false, the query is disabled regardless of clinic. Defaults to true. */
  enabled?: boolean;
}

/**
 * Fetch appointments with owner contacts/addresses for CSV export.
 * (Calendar/Flowboard use the "list" variant for performance, which omits addresses.)
 */
export async function fetchAppointmentsForCsv(params: {
  appointmentIds: string[];
}): Promise<AppointmentWithDetails[]> {
  const uniqueIds = Array.from(new Set(params.appointmentIds)).filter(Boolean);
  if (uniqueIds.length === 0) return [];

  const { data, error } = await supabase
    .from("appointments")
    .select(APPOINTMENTS_CSV_SELECT)
    .in("id", uniqueIds)
    .is("deleted_at", null)
    .is("appointment_pets.pet.deleted_at", null)
    .is("owner.deleted_at", null)
    .order("starts_at").returns<AppointmentWithRelationsRow[]>();

  if (error) throw error;

  const normalized = ((data ?? []) as AppointmentWithRelationsRow[])
    .map((row) => normalizeAppointmentRow(row, { includeItems: true }))
    .filter((appointment): appointment is AppointmentWithDetails => Boolean(appointment));

  return normalized as AppointmentWithDetails[];
}

// Listar agendamentos
export const useAppointments = (
  clinicId?: string,
  vetUserId?: string,
  startDate?: Date,
  endDate?: Date,
  options?: UseAppointmentsOptions
) => {
  const variant = options?.variant ?? "full";
  const allowWithoutClinic = options?.allowWithoutClinic ?? false;
  const clinicIds = options?.clinicIds && options.clinicIds.length > 0 ? options.clinicIds : null;
  const select = variant === "list" ? APPOINTMENTS_LIST_SELECT : APPOINTMENTS_FULL_SELECT;
  const startDateIso = startDate?.toISOString() ?? null;
  const endDateIso = endDate?.toISOString() ?? null;

  return useQuery({
    queryKey: ["appointments", clinicId ?? null, clinicIds, vetUserId ?? null, startDateIso, endDateIso, variant],
    queryFn: async () => {
      let query = supabase
        .from("appointments")
        .select(select);

      if (clinicIds) {
        query = query.in("clinic_id", clinicIds);
      } else if (clinicId) {
        query = query.eq("clinic_id", clinicId);
      }
      if (vetUserId) query = query.eq("vet_user_id", vetUserId);
      if (startDateIso) query = query.gte("starts_at", startDateIso);
      if (endDateIso) query = query.lte("starts_at", endDateIso);
      query = query.is("deleted_at", null);
      query = query.is("appointment_pets.pet.deleted_at", null);
      query = query.is("owner.deleted_at", null);

      const { data, error } = await query.order("starts_at").returns<AppointmentWithRelationsRow[]>();

      if (error) throw error;

      const normalized = ((data ?? []) as AppointmentWithRelationsRow[])
        .map((row) => normalizeAppointmentRow(row, { includeItems: true }))
        .filter((appointment): appointment is AppointmentWithDetails => Boolean(appointment));

      const linkedSalesByAppointmentId = await fetchLinkedSalesByAppointmentIds(
        normalized.map((appointment) => appointment.id),
      );

      return normalized.map((appointment) =>
        hydrateAppointmentFinancialFromLinkedSale({
          ...appointment,
          linked_sale: selectPreferredLinkedSale({
            sales: [
              appointment.linked_sale,
              linkedSalesByAppointmentId[appointment.id] ?? null,
            ].filter((sale): sale is LinkedSale => Boolean(sale)),
          }),
        }),
      ) as AppointmentWithDetails[];
    },
    enabled: (options?.enabled ?? true) && (allowWithoutClinic || !!clinicId || !!clinicIds),
    staleTime: 0,
    gcTime: 5 * 60_000,
    refetchOnMount: "always",
    refetchOnWindowFocus: true,
    refetchOnReconnect: true,
  });
};

// Obter detalhes de um agendamento
export const useAppointment = (id: string) => {
  return useQuery({
    queryKey: ["appointment", id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("appointments")
        .select(`
          *,
          clinic_appointment_type:clinic_appointment_types(id, name, color, icon, behavior),
          appointment_financial:appointment_financials(
            appointment_id,
            status,
            source,
            owner_package_id,
            sale_id,
            is_covered_by_package,
            updated_by,
            created_at,
            updated_at
          ),
          linked_sale:sales!sales_appointment_id_fkey(
            id,
            status,
            created_at,
            sale_payments(
              method,
              amount,
              received_at
            )
          ),
          recurrence:recurrence(id, frequency, interval, weekdays, monthday, starts_on, ends_on, max_occurrences, active, taxi_dog_type),
          appointment_pets(
            pet:pets(
              *,
              photo:pet_photos(file_path),
              species:species(name),
              breed:breeds(name),
              gender:pet_genders(name),
              sterilization:pet_sterilizations(name),
              coat_type:coat_types(name),
              color:colors(name),
              status:pet_statuses(name),
              pet_customer:pets_customers(notes)
            )
          ),
          owner:owners!inner(
            *,
            phones:owners_phones(*),
            emails:owners_emails(*),
            addresses:owners_addresses(*, city:cities(id, name, state_id, state:states(id, name, code)))
          ),
          vet:profiles!appointments_vet_user_id_fkey(id, full_name, avatar_url),
          appointment_items(
            id,
            appointment_id,
            item_id,
            qty,
            title_snapshot,
            created_at,
            updated_at,
            item:items(id, name, type, sale_price, track_by_batch, track_by_expiry)
          )
        ` as string)
        .eq("id", id)
        .is("deleted_at", null)
        .is("appointment_pets.pet.deleted_at", null)
        .is("owner.deleted_at", null)
        .limit(1).returns<AppointmentWithRelationsRow[]>();

      if (error) throw error;
      const appointmentRow = (Array.isArray(data) ? data[0] : null) as AppointmentWithRelationsRow | null;
      if (!appointmentRow) return null;
      const normalizedAppointment = normalizeDetailedAppointmentRow(appointmentRow);
      if (!normalizedAppointment) return null;

      const linkedSalesByAppointmentId = await fetchLinkedSalesByAppointmentIds([id]);
      return hydrateAppointmentFinancialFromLinkedSale({
        ...normalizedAppointment,
        linked_sale: selectPreferredLinkedSale({
          sales: [
            normalizedAppointment.linked_sale,
            linkedSalesByAppointmentId[id] ?? null,
          ].filter((sale): sale is LinkedSale => Boolean(sale)),
        }),
      });
    },
    enabled: !!id,
  });
};

export const createAppointmentWithOrder = async (input: AppointmentInput): Promise<Appointment> => {
  const { service_items = [], pet_ids: petIdsRaw = [], grooming_status: _legacyGroomingStatus, ...appointmentData } = input;
  const petIds = Array.from(new Set(petIdsRaw)).filter(Boolean);
  if (petIds.length === 0) throw new Error(i18n.t("calendar:phase8.appointments.errors.selectAtLeastOnePatient"));

  const { data: user } = await supabase.auth.getUser();
  if (!user.user) throw new Error(i18n.t("calendar:phase8.appointments.errors.notAuthenticated"));

  const appointmentPayload = {
    ...appointmentData,
    pet_id: petIds[0],
    vet_user_id:
      appointmentData.vet_user_id === undefined
        ? null
        : appointmentData.vet_user_id,
    notes: appointmentData.notes ?? null,
    is_taxi_dog: appointmentData.is_taxi_dog ?? false,
  };

  const { data: appointment, error: appointmentError } = await supabase
    .from("appointments")
    .insert({
      ...appointmentPayload,
      created_by_user_id: user.user.id,
    } as never)
    .select()
    .single();

  if (appointmentError) throw appointmentError;

  const itemsToInsert = service_items.filter((item) => item.item_id);
  const relationsPayload = petIds.map((pet_id) => ({
    appointment_id: appointment.id,
    pet_id,
  }));

  const [relationResult, itemsResult] = await Promise.all([
    supabase
      .from("appointment_pets")
      .insert(relationsPayload),
    itemsToInsert.length > 0
      ? supabase
          .from("appointment_items")
          .insert(
            itemsToInsert.map((item) => ({
              appointment_id: appointment.id,
              item_id: item.item_id!,
              qty: item.qty,
              title_snapshot: item.title_snapshot || "",
            })),
          )
      : Promise.resolve({ error: null }),
  ]);

  if (relationResult.error) throw relationResult.error;
  if (itemsResult.error) throw itemsResult.error;

  return appointment as Appointment;
};

// Criar agendamento
export const useCreateAppointment = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("calendar");

  return useMutation({
    mutationFn: createAppointmentWithOrder,
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      queryClient.invalidateQueries({ queryKey: ["grooming-appointments"] });
      invalidateReportsCache(queryClient);
      invalidatePatientsCache(queryClient, {
        ownerId: variables?.owner_id ?? null,
        petIds: variables?.pet_ids ?? null,
      });
      toast({
        title: t("phase8.appointments.createdTitle"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("phase8.appointments.createErrorTitle"),
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

// Atualizar agendamento
export const useUpdateAppointment = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("calendar");

  return useMutation({
    mutationFn: async ({
      id,
      input,
    }: {
      id: string;
    input: Partial<AppointmentInput> & { pet_ids?: string[] };
    }) => {
      const { data: currentAppointment, error: currentError } = await supabase
        .from("appointments")
        .select(
          `
          type,
          owner_id,
          pet_id,
          clinic_appointment_type:clinic_appointment_types(behavior),
          appointment_pets(pet_id)
        `,
        )
        .eq("id", id)
        .single();
      if (currentError) throw currentError;

      const { pet_ids, grooming_status: _legacyGroomingStatus, ...appointmentFields } = input as Partial<AppointmentInput> & {
        pet_ids?: unknown;
      };

      const petIds: string[] | undefined =
        pet_ids === undefined
          ? undefined
          : Array.isArray(pet_ids)
            ? Array.from(new Set(pet_ids)).filter((id): id is string => typeof id === "string" && Boolean(id))
            : undefined;

      const updatePayload: Partial<AppointmentInput> & { pet_id?: string | null } = { ...appointmentFields };
      if (petIds && petIds.length > 0) {
        updatePayload.pet_id = petIds[0];
      }

      const { error } = await supabase
        .from("appointments")
        .update(updatePayload as never)
        .eq("id", id);

      if (error) throw error;

      const shouldClearBelongings =
        (currentAppointment as unknown as { clinic_appointment_type?: { behavior?: string } | null })?.clinic_appointment_type?.behavior === "banho_tosa" &&
        updatePayload.type !== undefined;

      if (petIds) {
        if (petIds.length === 0) throw new Error(t("phase8.appointments.errors.selectAtLeastOnePatient"));
      }

      const [deleteBelongingsResult, deletePetsResult] = await Promise.all([
        shouldClearBelongings
          ? supabase
              .from("appointment_belongings")
              .delete()
              .eq("appointment_id", id)
          : Promise.resolve({ error: null }),
        petIds
          ? supabase
              .from("appointment_pets")
              .delete()
              .eq("appointment_id", id)
          : Promise.resolve({ error: null }),
      ]);

      if (deleteBelongingsResult.error) throw deleteBelongingsResult.error;
      if (deletePetsResult.error) throw deletePetsResult.error;

      if (petIds) {

        const relationsPayload = petIds.map((pet_id) => ({
          appointment_id: id,
          pet_id,
        }));
        const { error: insertError } = await supabase
          .from("appointment_pets")
          .insert(relationsPayload);
        if (insertError) throw insertError;
      }

      const ownerId = (input as Partial<AppointmentInput>).owner_id ?? currentAppointment?.owner_id ?? null;
      const existingPetIds = extractPetIdsFromAppointmentPets(currentAppointment?.appointment_pets);
      const fallbackPetId = typeof currentAppointment?.pet_id === "string" ? currentAppointment.pet_id : null;
      const effectivePetIds = petIds && petIds.length > 0
        ? petIds
        : (existingPetIds.length > 0 ? existingPetIds : (fallbackPetId ? [fallbackPetId] : []));

      return { id, ownerId, petIds: effectivePetIds };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      queryClient.invalidateQueries({ queryKey: ["appointment", result.id] });
      queryClient.invalidateQueries({ queryKey: ["appointment-belongings", result.id] });
      queryClient.invalidateQueries({ queryKey: ["appointment-patient-packages", result.id] });
      queryClient.invalidateQueries({ queryKey: ["grooming-appointments"] });
      invalidateReportsCache(queryClient);
      invalidatePatientsCache(queryClient, { ownerId: result.ownerId, petIds: result.petIds });
      toast({
        title: t("phase8.appointments.updatedTitle"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("phase8.appointments.updateErrorTitle"),
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

// Atualizar status do agendamento
export const useUpdateAppointmentStatus = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("calendar");

  return useMutation({
    mutationFn: async ({ id, status }: { id: string; status: Appointment["status"] }) => {
      const normalizedStatus: AppointmentStatus = status === "pago" ? "finalizado" : status;

      const { data: appointment, error: fetchError } = await supabase
        .from("appointments")
        .select(
          `
          type,
          owner_id,
          pet_id,
          appointment_pets(pet_id),
          appointment_financial:appointment_financials(status)
        `,
        )
        .eq("id", id)
        .single();

      if (fetchError) throw fetchError;

      const payload: { status: string; updated_at?: string } = {
        status: normalizedStatus,
      };

      payload.updated_at = new Date().toISOString();

      const { error } = await supabase
        .from("appointments")
        .update(payload)
        .eq("id", id);

      if (error) throw error;

      const petIds = extractPetIdsFromAppointmentPets(appointment?.appointment_pets);
      const fallbackPetId = typeof appointment?.pet_id === "string" ? appointment.pet_id : null;
      const effectivePetIds = petIds.length > 0 ? petIds : (fallbackPetId ? [fallbackPetId] : []);
      const ownerId = typeof appointment?.owner_id === "string" ? appointment.owner_id : null;
      return { id, ownerId, petIds: effectivePetIds };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      queryClient.invalidateQueries({ queryKey: ["appointment", result.id] });
      queryClient.invalidateQueries({ queryKey: ["appointment-patient-packages", result.id] });
      queryClient.invalidateQueries({ queryKey: ["grooming-appointments"] });
      invalidateSalesCache(queryClient, { appointmentId: result.id });
      invalidateReportsCache(queryClient);
      invalidatePatientsCache(queryClient, { ownerId: result.ownerId, petIds: result.petIds });
      toast({
        title: t("phase8.appointments.statusUpdatedTitle"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("phase8.appointments.statusUpdateErrorTitle"),
        description: error.message,
        variant: "destructive",
      });
    },
  });
};

// Deletar agendamento (soft delete via RPC: bypassa RLS; deleted_at + deleted_by, auditado pelo trigger)
export const useDeleteAppointment = () => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { t } = useTranslation("calendar");

  return useMutation({
    mutationFn: async (id: string) => {
      const [appointmentResult, deleteResult] = await Promise.all([
        supabase
          .from("appointments")
          .select(
            `
            owner_id,
            pet_id,
            appointment_pets(pet_id)
          `,
          )
          .eq("id", id)
          .maybeSingle(),
        supabase.rpc("soft_delete_appointment", {
          p_appointment_id: id,
        }),
      ]);

      if (appointmentResult.error) throw appointmentResult.error;
      if (deleteResult.error) throw deleteResult.error;
      const appointment = appointmentResult.data;
      const petIds = extractPetIdsFromAppointmentPets(appointment?.appointment_pets);
      const fallbackPetId = typeof appointment?.pet_id === "string" ? appointment.pet_id : null;
      const effectivePetIds = petIds.length > 0 ? petIds : (fallbackPetId ? [fallbackPetId] : []);
      const ownerId = typeof appointment?.owner_id === "string" ? appointment.owner_id : null;
      return { id, ownerId, petIds: effectivePetIds };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ["appointments"] });
      queryClient.invalidateQueries({ queryKey: ["appointment-patient-packages", result.id] });
      queryClient.invalidateQueries({ queryKey: ["grooming-appointments"] });
      invalidateReportsCache(queryClient);
      invalidatePatientsCache(queryClient, { ownerId: result.ownerId, petIds: result.petIds });
      toast({
        title: t("phase8.appointments.deletedTitle"),
      });
    },
    onError: (error: Error) => {
      toast({
        title: t("phase8.appointments.deleteErrorTitle"),
        description: error.message,
        variant: "destructive",
      });
    },
  });
};
